import { Box, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import React, { FC, memo, useCallback } from 'react';
import { connect } from 'react-redux';
import CustomDataGrid, { CellWrapper } from 'src/components/DataGrid';
import { RootState } from 'src/store';
import FolderIcon from '@mui/icons-material/FolderOpenOutlined';
import {
  ReportsItem,
  ReportsStatus,
} from 'src/routes/Reports/store/reportsViewSlice/types';
import { getReportsViewDataReports } from 'src/routes/Reports/store/reportsViewSlice';
import { transition } from 'src/theme';

const columns: GridColDef<ReportsItem[][number]>[] = [
  {
    field: 'title',
    headerName: 'שם',
    align: 'left',
    headerAlign: 'left',
    width: 225,
    renderCell: ({ value }) => (
      <CellWrapper>
        <Typography variant="body1" color="text.primary" fontWeight={600}>
          {value as string}
        </Typography>
      </CellWrapper>
    ),
  },
  {
    field: 'from_date',
    headerName: 'מתאריך',
    align: 'left',
    headerAlign: 'left',
    width: 150,
    renderCell: ({ value }) => (
      <CellWrapper>
        <Typography variant="body1" color="text.primary" fontWeight={600}>
          {dayjs(value as string).format('DD/MM/YYYY')}
        </Typography>
      </CellWrapper>
    ),
  },
  {
    field: 'to_date',
    headerName: 'עד תאריך',
    align: 'left',
    headerAlign: 'left',
    width: 150,
    renderCell: ({ value }) => (
      <CellWrapper>
        <Typography variant="body1" color="text.primary" fontWeight={600}>
          {dayjs(value as string).format('DD/MM/YYYY')}
        </Typography>
      </CellWrapper>
    ),
  },
  {
    field: 'receipts_count',
    headerName: 'חשבוניות',
    align: 'left',
    headerAlign: 'left',
    width: 85,
    renderCell: ({ value }) => (
      <CellWrapper>
        <Typography variant="body1" color="text.primary" fontWeight={600}>
          {(value as number).toLocaleString()}
        </Typography>
      </CellWrapper>
    ),
  },
  {
    field: 'sent_to',
    headerName: 'נשלח ל',
    align: 'left',
    headerAlign: 'left',
    width: 200,
    renderCell: ({ value }) => {
      const emailsCount = (value as string[]).length;
      return (
        <CellWrapper>
          <Typography
            variant="body1"
            color="text.primary"
            fontWeight={600}
            fontSize={emailsCount === 1 ? '0.875rem' : '1rem'}
            sx={{
              textDecoration: 'underline',
            }}
          >
            {emailsCount === 1
              ? (value as string[])[0]
              : `${emailsCount} כתובות`}
          </Typography>
        </CellWrapper>
      );
    },
  },
  {
    field: 'status',
    headerName: 'סטאטוס',
    align: 'left',
    headerAlign: 'left',
    width: 100,
    renderCell: ({ value }) => (
      <Stack
        direction="row"
        gap={1}
        height="100%"
        width="100%"
        alignItems="center"
        justifyContent="flex-start"
      >
        <Box
          sx={{
            boxShadow: ({ shadows }) => shadows[0],
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor:
              value === ReportsStatus.Sent
                ? 'lightgreen'
                : value === ReportsStatus.Pending
                  ? 'orange'
                  : 'red',
          }}
        />
        <Typography variant="body1" color="text.primary" fontWeight={600}>
          {value === ReportsStatus.Sent
            ? 'נשלח'
            : value === ReportsStatus.Pending
              ? 'ממתין'
              : 'קרה שגיאה'}
        </Typography>
      </Stack>
    ),
  },
  {
    field: 'view',
    headerName: '',
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    flex: 1,
    renderCell: ({ value }) => (
      <CellWrapper
        justifyContent="center"
        sx={{
          opacity: 0.5,
          transition: transition(),
          cursor: 'pointer',
          '&:hover': {
            opacity: 1,
            '& svg': {
              color: ({ palette }) => palette.primary.main,
            },
          },
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <FolderIcon sx={{ transition: transition() }} />
          <Typography
            variant="body1"
            color="text.primary"
            fontWeight={600}
            fontSize="1.125rem"
          >
            לצפייה
          </Typography>
        </Box>
      </CellWrapper>
    ),
  },
];

type Props = ReturnType<typeof mapStateToProps>;

const ReportsResults: FC<Props> = ({ reports }) => {
  console.log({ reports });

  const handleActionClick = useCallback((id: string | number) => {
    console.log('Action clicked for report:', id);
    // Add your action handling logic here
  }, []);

  return (
    <CustomDataGrid
      rows={reports ?? []}
      columns={columns}
      withActionsMenu
      onActionClick={handleActionClick}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  reports: getReportsViewDataReports(state),
});

export default connect(mapStateToProps)(memo(ReportsResults, isEqual));
